// Pure shaping for the Stablecoins screen: table rows with shares and chain
// splits, the chain league, history points for the chart, and the labels
// for mechanisms and depeg states. No network, no DOM, so every rule is
// tested directly. A missing figure stays null here and renders as
// "unavailable" or "—" in the component, never as zero.

import { compactUsd } from "./format";
import type { DepegStatus, Stablecoin, StablecoinChain, StablecoinCurrencyTotal, Stablecoins, StablecoinsTotals, SupplySource } from "./stablecoins";

const DAY_MS = 86_400_000;

/** Chains drawn as their own segment in a row's distribution bar; the rest are "other". */
export const TOP_CHAINS_PER_ROW = 4;

// ---- labels ----

const MECHANISM_LABELS: Record<string, string> = {
  "fiat-backed": "Fiat-backed",
  "crypto-backed": "Crypto-backed",
  // DefiLlama spells one asset's mechanism this way.
  "crytpo-backed": "Crypto-backed",
  algorithmic: "Algorithmic",
};

/** A readable mechanism, or null when the API gives none. */
export function mechanismLabel(raw: string | null | undefined): string | null {
  const key = (raw ?? "").trim().toLowerCase();
  if (!key) return null;
  return MECHANISM_LABELS[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
}

export type BadgeTone = "muted" | "warn" | "down" | "unknown";

export interface DepegBadge {
  label: string;
  tone: BadgeTone;
  title: string;
}

const DEPEG_BADGES: Record<DepegStatus, DepegBadge> = {
  on_peg: { label: "on peg", tone: "muted", title: "Price within the peg band" },
  minor: { label: "minor", tone: "warn", title: "Price drifting from the peg" },
  depegged: { label: "depegged", tone: "down", title: "Price broke its peg" },
  unknown: { label: "unknown", tone: "unknown", title: "No price reading" },
};

/** Badge copy and tone for a depeg state; anything unexpected reads as unknown. */
export function depegBadge(status: DepegStatus | string | null | undefined): DepegBadge {
  return DEPEG_BADGES[status as DepegStatus] ?? DEPEG_BADGES.unknown;
}

/** Sort key: the worse the state, the higher; unknown sorts last either way (null). */
export function depegRank(status: DepegStatus | string | null | undefined): number | null {
  switch (status) {
    case "depegged":
      return 2;
    case "minor":
      return 1;
    case "on_peg":
      return 0;
    default:
      return null;
  }
}

/** "$1.0003": four decimals, since a peg lives in the fourth place. */
export function priceText(price: number | null | undefined): string {
  if (price === null || price === undefined || !Number.isFinite(price)) return "unavailable";
  return `$${price.toFixed(4)}`;
}

/** "45.3%" for shares at or above a tenth; two decimals below. */
export function shareText(share: number): string {
  return `${(share * 100).toFixed(share >= 0.1 ? 1 : 2)}%`;
}

// ---- table rows ----

export interface ChainSegment {
  chain: string;
  marketCap: number;
  /** Of the row's chain total. */
  share: number;
  /** DQ-23: Panther read this chain's supply itself. */
  onchain: boolean;
}

export interface StablecoinRow {
  id: string;
  symbol: string;
  name: string;
  peg: string;
  /** Peg currency, "USD", "EUR"…; read from `peg` when the API gives none. */
  currency: string;
  /** Readable mechanism; null when unknown. */
  mechanism: string | null;
  price: number | null;
  marketCap: number | null;
  /** Of the all-currency total; null when either figure is unknown. */
  share: number | null;
  change24h: number | null;
  change7d: number | null;
  change30d: number | null;
  depeg: DepegStatus;
  depegRank: number | null;
  /** Chains with supply, largest first, each with its share of the row's chain total. */
  chains: ChainSegment[];
  /** The largest `TOP_CHAINS_PER_ROW` chains plus one "Other" segment when more exist. */
  segments: ChainSegment[];
  chainCount: number;
  /** DQ-16: the value is held by the jump guard, with why; null when DefiLlama's value is used as it is. */
  held: { label: string; title: string } | null;
  /** DQ-23: the market cap comes from Panther's own chain reads, with DefiLlama's figure in the title; null otherwise. */
  onchain: { title: string } | null;
  defillamaCap: number | null;
}

const finite = (value: number | null | undefined): value is number => value !== null && value !== undefined && Number.isFinite(value);

/** Chains with positive supply, largest first, as shares of their own sum. */
export function chainSplit(chains: ReadonlyArray<{ chain: string; market_cap: number | null; source?: SupplySource }>): ChainSegment[] {
  const known = chains.filter((c): c is { chain: string; market_cap: number; source?: SupplySource } => finite(c.market_cap) && c.market_cap > 0);
  const total = known.reduce((sum, c) => sum + c.market_cap, 0);
  if (!(total > 0)) return [];
  return known
    .map((c) => ({ chain: c.chain, marketCap: c.market_cap, share: c.market_cap / total, onchain: c.source === "onchain" }))
    .sort((a, b) => b.marketCap - a.marketCap);
}

/** Top chains as their own segments, the rest summed as "Other". */
export function barSegments(chains: readonly ChainSegment[], top = TOP_CHAINS_PER_ROW): ChainSegment[] {
  if (chains.length <= top) return [...chains];
  const shown = chains.slice(0, top);
  const rest = chains.slice(top);
  return [
    ...shown,
    {
      chain: `Other (${rest.length})`,
      marketCap: rest.reduce((sum, c) => sum + c.marketCap, 0),
      share: rest.reduce((sum, c) => sum + c.share, 0),
      onchain: false,
    },
  ];
}

/** A coin's peg currency: the API's `currency`, else its `peg`. */
export const coinCurrency = (coin: Pick<Stablecoin, "currency" | "peg">): string => coin.currency || coin.peg;

/** `total` is the market cap of every stablecoin, all currencies, so shares add up across the table. */
export function stablecoinRow(coin: Stablecoin, total: number | null): StablecoinRow {
  const chains = chainSplit(coin.chains ?? []);
  const cap = finite(coin.market_cap) ? coin.market_cap : null;
  const share = cap !== null && finite(total) && total > 0 ? cap / total : null;
  return {
    id: coin.id,
    symbol: coin.symbol,
    name: coin.name,
    peg: coin.peg,
    currency: coinCurrency(coin),
    mechanism: mechanismLabel(coin.peg_mechanism),
    price: finite(coin.price) ? coin.price : null,
    marketCap: cap,
    share,
    change24h: finite(coin.change_24h) ? coin.change_24h : null,
    change7d: finite(coin.change_7d) ? coin.change_7d : null,
    change30d: finite(coin.change_30d) ? coin.change_30d : null,
    // A state this screen does not know reads as unknown rather than crashing a badge.
    depeg: depegRank(coin.depeg_status) === null ? "unknown" : coin.depeg_status,
    depegRank: depegRank(coin.depeg_status),
    chains,
    segments: barSegments(chains),
    chainCount: chains.length,
    held: heldNote(coin),
    onchain: onchainNote(coin),
    defillamaCap: finite(coin.defillama_market_cap) ? coin.defillama_market_cap : null,
  };
}

/** The on-chain mark's hover: how much was read on chain, and DefiLlama's figure to compare. */
export function onchainNote(coin: Pick<Stablecoin, "source" | "market_cap" | "defillama_market_cap" | "onchain_share">): { title: string } | null {
  if (coin.source !== "onchain") return null;
  const share = finite(coin.onchain_share) ? coin.onchain_share : null;
  let title =
    share === null
      ? "Panther reads this supply on chain itself; any chains it does not read are as DefiLlama reports them."
      : share >= 0.9995
        ? "Panther reads all of this supply on chain itself."
        : `Panther reads ${(share * 100).toFixed(1)}% of this supply on chain itself; the other chains are as DefiLlama reports them.`;
  if (finite(coin.defillama_market_cap) && coin.defillama_market_cap > 0 && finite(coin.market_cap)) {
    const gap = coin.market_cap / coin.defillama_market_cap - 1;
    title += ` DefiLlama: ${compactUsd(coin.defillama_market_cap)}, ${Math.abs(gap * 100).toFixed(2)}% ${gap > 0 ? "below" : "above"} ours.`;
  }
  return { title };
}

/** The as-of line's lead-in before "DefiLlama for the rest"; null when nothing was read on chain. */
export function onchainSourceText(totals: Pick<StablecoinsTotals, "onchain" | "onchain_share">): string | null {
  const coins = totals.onchain ?? 0;
  if (coins <= 0) return null;
  const share = finite(totals.onchain_share) ? ` (${Math.round(totals.onchain_share * 100)}% of value)` : "";
  return `on-chain reads for ${coins} ${coins === 1 ? "coin" : "coins"}${share}`;
}

/** "Hyperliquid L1, Solana and Monad" */
const listText = (items: readonly string[]): string => (items.length < 2 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`);

/**
 * The "held" badge for a coin the DQ-16 jump guard holds: DefiLlama dropped a chain (its last
 * confirmed value is kept until on-chain data agrees) or moved more than 5% without confirmation.
 * Null when DefiLlama's value is used as it is.
 */
export function heldNote(coin: Pick<Stablecoin, "symbol" | "name" | "stale_reason" | "chains_held" | "held_since" | "candidate_market_cap" | "candidate_change">): { label: string; title: string } | null {
  if (!coin.stale_reason) return null;
  const name = coin.symbol || coin.name;
  const since = coin.held_since && Number.isFinite(Date.parse(coin.held_since)) ? ` Held since ${coin.held_since.slice(0, 16).replace("T", " ")} UTC.` : "";
  const chains = (coin.chains_held ?? []).filter(Boolean);
  if (coin.stale_reason === "upstream_chain_held" && chains.length) {
    return {
      label: "held",
      title: `DefiLlama stopped reporting ${name} on ${listText(chains)}; showing the last confirmed value until on-chain data agrees.${since}`,
    };
  }
  const move = finite(coin.candidate_change) ? ` (${coin.candidate_change > 0 ? "+" : "−"}${Math.abs(coin.candidate_change * 100).toFixed(1)}%)` : "";
  return {
    label: "held",
    title: `DefiLlama's latest value for ${name}${move} is not confirmed yet; showing the last accepted value until the move holds or on-chain data agrees.${since}`,
  };
}

/** Every stablecoin as a table row, largest market cap first; unknown caps last. */
export function stablecoinRows(data: Pick<Stablecoins, "stablecoins" | "totals">): StablecoinRow[] {
  const total = data.totals.market_cap;
  return data.stablecoins
    .map((coin) => stablecoinRow(coin, total))
    .sort((a, b) => {
      if (a.marketCap === null || b.marketCap === null) return (a.marketCap === null ? 1 : 0) - (b.marketCap === null ? 1 : 0);
      return b.marketCap - a.marketCap;
    });
}

// ---- currencies ----

/** The table's currency filter: one ISO currency, or every one. */
export type CurrencyFilter = "all" | (string & {});

export const DEFAULT_CURRENCY: CurrencyFilter = "USD";

export function filterByCurrency<T extends { currency: string }>(rows: readonly T[], currency: CurrencyFilter): T[] {
  return currency === "all" ? [...rows] : rows.filter((row) => row.currency === currency);
}

export interface CurrencyRow {
  currency: string;
  stablecoins: number;
  marketCap: number | null;
  /** Of the all-currency total. */
  share: number | null;
}

/**
 * The total by peg currency, largest first, unknown caps last. From the API's
 * `by_currency` where it publishes one, else summed from the coins.
 */
export function currencyRows(data: Pick<Stablecoins, "stablecoins" | "totals">): CurrencyRow[] {
  const published: StablecoinCurrencyTotal[] | undefined = data.totals.by_currency;
  let rows: CurrencyRow[];
  if (published?.length) {
    rows = published.map((c) => ({
      currency: c.currency,
      stablecoins: c.stablecoins,
      marketCap: finite(c.market_cap) ? c.market_cap : null,
      share: finite(c.share) ? c.share : null,
    }));
  } else {
    const byCurrency = new Map<string, { stablecoins: number; caps: number[] }>();
    for (const coin of data.stablecoins) {
      const entry = byCurrency.get(coinCurrency(coin)) ?? { stablecoins: 0, caps: [] };
      entry.stablecoins += 1;
      if (finite(coin.market_cap)) entry.caps.push(coin.market_cap);
      byCurrency.set(coinCurrency(coin), entry);
    }
    const total = data.totals.market_cap;
    rows = [...byCurrency].map(([currency, { stablecoins, caps }]) => {
      const marketCap = caps.length ? caps.reduce((sum, cap) => sum + cap, 0) : null;
      return { currency, stablecoins, marketCap, share: marketCap !== null && finite(total) && total > 0 ? marketCap / total : null };
    });
  }
  return rows.sort((a, b) => {
    if (a.marketCap === null || b.marketCap === null) return (a.marketCap === null ? 1 : 0) - (b.marketCap === null ? 1 : 0);
    return b.marketCap - a.marketCap;
  });
}

export interface CurrencyOption {
  value: CurrencyFilter;
  label: string;
}

/** The filter's choices: USD first (the default), then all currencies, then each other currency by size. */
export function currencyOptions(currencies: readonly CurrencyRow[], total: number): CurrencyOption[] {
  const usd = currencies.find((c) => c.currency === "USD");
  const others = currencies.filter((c) => c.currency !== "USD");
  const option = (c: CurrencyRow): CurrencyOption => ({ value: c.currency, label: `${c.currency} (${c.stablecoins})` });
  return [...(usd ? [option(usd)] : []), { value: "all", label: `All currencies (${total})` }, ...others.map(option)];
}

/** Stablecoins the API marks as drifting but not broken. */
export function minorCount(data: Pick<Stablecoins, "stablecoins">): number {
  return data.stablecoins.filter((coin) => coin.depeg_status === "minor").length;
}

// ---- chain league ----

export interface ChainRow {
  chain: string;
  marketCap: number | null;
  share: number | null;
  stablecoins: number | null;
}

/**
 * Chains ranked by market cap, unknown caps last. The API's share is kept
 * where it gives one; otherwise it is the chain's part of the summed caps.
 */
export function chainRows(data: Pick<Stablecoins, "chains">): ChainRow[] {
  const chains: StablecoinChain[] = data.chains ?? [];
  const total = chains.reduce((sum, c) => sum + (finite(c.market_cap) ? c.market_cap : 0), 0);
  return chains
    .map((c) => {
      const marketCap = finite(c.market_cap) ? c.market_cap : null;
      const share = finite(c.share) ? c.share : marketCap !== null && total > 0 ? marketCap / total : null;
      return { chain: c.chain, marketCap, share, stablecoins: finite(c.stablecoins) ? c.stablecoins : null };
    })
    .sort((a, b) => {
      if (a.marketCap === null || b.marketCap === null) return (a.marketCap === null ? 1 : 0) - (b.marketCap === null ? 1 : 0);
      return b.marketCap - a.marketCap;
    });
}

// ---- history ----

/** [epoch ms, market cap]. */
export type Point = [number, number];

/** Readable history points in time order, one per timestamp; unreadable ones are dropped, never zeroed. */
/** Days whose figure holds an unconfirmed upstream value (DQ-16), for the chart tooltip. */
export function unconfirmedTimes(data: Pick<Stablecoins, "history">): Set<number> {
  return new Set((data.history ?? []).filter((p) => p.quality === "upstream_unconfirmed").map((p) => Date.parse(p.t)).filter(Number.isFinite));
}

export function historyPoints(data: Pick<Stablecoins, "history">): Point[] {
  const byTime = new Map<number, number>();
  for (const point of data.history ?? []) {
    const t = Date.parse(point.t);
    if (!Number.isFinite(t) || !finite(point.market_cap)) continue;
    byTime.set(t, point.market_cap);
  }
  return [...byTime].sort((a, b) => a[0] - b[0]);
}

export const RANGES = [
  { id: "30d", label: "30D", days: 30 },
  { id: "90d", label: "90D", days: 90 },
  { id: "1y", label: "1Y", days: 365 },
  { id: "all", label: "All", days: null },
] as const;

export type RangeId = (typeof RANGES)[number]["id"];

/** The points within `days` of the newest one; all of them when `days` is null. */
export function rangePoints(points: readonly Point[], days: number | null): Point[] {
  const last = points[points.length - 1];
  if (days === null || !last) return [...points];
  const since = last[0] - days * DAY_MS;
  return points.filter(([t]) => t >= since);
}
